import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import {
  SMS_COST_KIND,
  clearAllSmsCostIssues,
  clearSmsCostIssue,
  detectSmsCostIssues,
} from '#/shared/lib/sms-cost'
import type { SmsCostIssue, SmsCostKind } from '#/shared/lib/sms-cost'
import { computeSmsSegments } from '#/shared/lib/sms-segments'
import { cn } from '#/shared/lib/utils'
import { AlertTriangleIcon, CloseSmallIcon } from '#/shared/ui/icons'
import { Tooltip } from '#/shared/ui/tooltip'

const LABEL_KEY: Record<SmsCostKind, string> = {
  [SMS_COST_KIND.formatting]: 'smsCost.kind.formatting',
  [SMS_COST_KIND.bulletList]: 'smsCost.kind.bulletList',
  [SMS_COST_KIND.emoji]: 'smsCost.kind.emoji',
  [SMS_COST_KIND.smartPunctuation]: 'smsCost.kind.smartPunctuation',
  [SMS_COST_KIND.otherUnicode]: 'smsCost.kind.otherUnicode',
  [SMS_COST_KIND.gsmExtended]: 'smsCost.kind.gsmExtended',
}

/** What clearing this kind actually does — shown on hover so the agent isn't
 *  guessing whether their text survives the fix. */
const HINT_KEY: Record<SmsCostKind, string> = {
  [SMS_COST_KIND.formatting]: 'smsCost.hint.formatting',
  [SMS_COST_KIND.bulletList]: 'smsCost.hint.bulletList',
  [SMS_COST_KIND.emoji]: 'smsCost.hint.emoji',
  [SMS_COST_KIND.smartPunctuation]: 'smsCost.hint.smartPunctuation',
  [SMS_COST_KIND.otherUnicode]: 'smsCost.hint.otherUnicode',
  [SMS_COST_KIND.gsmExtended]: 'smsCost.hint.gsmExtended',
}

/** Chip for one offender: the characters themselves where there are any (so the
 *  agent sees exactly what to look for in their message), else a named label. */
function CostChip({
  issue,
  onClear,
}: {
  issue: SmsCostIssue
  onClear: () => void
}) {
  const { t } = useTranslation()
  const label = t(LABEL_KEY[issue.kind])
  const chars = issue.samples.join('')
  return (
    <Tooltip content={t(HINT_KEY[issue.kind])}>
      <span className="flex items-center gap-1 rounded-full border border-orange-200 bg-orange-50 py-0.5 pr-0.5 pl-2 text-orange-700">
        {chars ? (
          <span className="max-w-30 truncate font-medium" aria-hidden>
            {chars}
          </span>
        ) : null}
        <span className={cn(chars && 'sr-only')}>{label}</span>
        <span className="tabular-nums opacity-70">×{issue.count}</span>
        <button
          type="button"
          onClick={onClear}
          aria-label={t('smsCost.clear', { label })}
          className="flex size-4.5 cursor-pointer items-center justify-center rounded-full text-orange-700 transition-colors hover:bg-orange-200"
        >
          <CloseSmallIcon className="size-3" />
        </button>
      </span>
    </Tooltip>
  )
}

/**
 * Shows what is pushing a message past one segment and clears it — per offender
 * or all at once. Only appears once the message actually spans more than one
 * segment, so a short formatted message (which costs nothing extra) stays quiet.
 *
 * `html` is the editor's own markup, not the sent body: formatting and bullet
 * lists are the usual culprits and only exist there. The cleaned HTML goes back
 * through `onChange`, which the composers feed straight into the editor.
 */
export function SmsCostPanel({
  html,
  plainText,
  onChange,
  className,
}: {
  html: string
  /** The body that will actually be sent — its segment count gates the panel. */
  plainText: string
  onChange: (html: string) => void
  className?: string
}) {
  const { t } = useTranslation()
  const segments = useMemo(
    () => computeSmsSegments(plainText).segments,
    [plainText],
  )
  const issues = useMemo(() => detectSmsCostIssues(html), [html])

  if (segments <= 1 || issues.length === 0) return null

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-1 rounded-lg bg-orange-50/60 px-2 py-1.5 text-xs',
        className,
      )}
    >
      <AlertTriangleIcon className="size-3.5 shrink-0 text-orange-500" />
      <span className="mr-0.5 text-warm-500">{t('smsCost.title')}</span>
      {issues.map((issue) => (
        <CostChip
          key={issue.kind}
          issue={issue}
          onClear={() => onChange(clearSmsCostIssue(html, issue.kind))}
        />
      ))}
      {issues.length > 1 && (
        <button
          type="button"
          onClick={() => onChange(clearAllSmsCostIssues(html))}
          className="ml-auto cursor-pointer rounded-md px-1.5 py-0.5 font-medium text-orange-700 transition-colors hover:bg-orange-100"
        >
          {t('smsCost.clearAll')}
        </button>
      )}
    </div>
  )
}
